/**
 * LightingSystem - sol com sombra que acompanha o jogador, preenchimento
 * hemisferico fraco e pool de luzes dinamicas para os efeitos.
 *
 * O sol NAO tem cor nem intensidade proprias: le tudo de ctx.sky. Assim a luz
 * direta, o environment map e a neblina saem do mesmo modelo atmosferico e nao
 * ha como um brigar com o outro.
 *
 * SOMBRA - o item mais visivel do conjunto. Tres decisoes valem explicacao:
 *
 * 1. O frustum ortografico segue a camera e e ARREDONDADO EM TEXELS na base da
 *    propria luz. Sem isso a sombra "ferve" (shadow shimmering) a cada passo do
 *    jogador, porque os texels caem em posicoes diferentes do mundo a cada frame.
 *
 * 2. O centro do frustum e deslocado PARA A FRENTE do olhar. Metade do orcamento
 *    de textura estaria atras da cabeca do jogador; empurrando o centro, a mesma
 *    textura cobre bem mais do que se ve. O deslocamento e amortecido para o giro
 *    do mouse nao arrastar a regiao de sombra de uma vez. Quando o frustum ja
 *    cobre a diagonal do cenario (preset ultra), ele para de seguir e fica
 *    ancorado no centro do mapa - ver _refreshCoverageMode.
 *
 * 3. NAO ha cascata de verdade (CSM). Cascata em three exige remendar o shader de
 *    TODOS os materiais da cena (three/addons/csm patcha material por material) -
 *    e os materiais deste projeto sao de outro modulo, criados depois deste, e
 *    ainda ha os dos bots e dos efeitos, criados em tempo de jogo. Um material que
 *    escapasse do patch renderizaria com sombra errada ou nem compilaria. Em vez
 *    disso o frustum cobre a shadowDistance inteira do preset (60 a 160 m), o que
 *    da texel de 3.9 a 5.9 cm - mais grosso do que a versao anterior, que era
 *    nitida e simplesmente NAO TINHA sombra alem de ~47 m.
 *
 * POOL DE LUZES: as PointLights ficam SEMPRE na cena, com intensidade 0 quando
 * ociosas. Ligar e desligar o .visible mudaria a contagem de luzes e forcaria o
 * three a recompilar todos os shaders - engasgo garantido a cada tiro.
 */

import * as THREE from 'three';

/** Teto de luzes dinamicas simultaneas (contrato do ARCHITECTURE 4.4). */
const MAX_DYNAMIC_LIGHTS = 6;
const DYNAMIC_BY_QUALITY = { low: 3, medium: 4, high: 6, ultra: 6 };

/** Usado so se ctx.sky ainda for stub: mesmo clima, valores fixos.
 *  Atualizados junto com o rebalanco sol/ceu do Sky.js (SUN_ILLUMINANCE 3.2 e
 *  SUN_WHITE_MIX 0.18): se o stub entregasse os numeros antigos, a cena piscaria
 *  de cor e de intensidade no primeiro frame em que o ceu de verdade assumisse. */
const FALLBACK_SUN_DIR = [0.8532, 0.2588, -0.4529];
const FALLBACK_SUN_RGB = [1.0, 0.562, 0.255];
const FALLBACK_SUN_INTENSITY = 5.05;

/**
 * Preenchimento hemisferico. Fraco de proposito: o grosso vem do environment.
 *
 * POR QUE A SOMBRA EXTERNA SAIA CINZA (medido: reboco na sombra 45/45/47, dois
 * niveis de azul). Numa parede VERTICAL a HemisphereLight entrega metade cor do
 * ceu e metade cor do chao. A cor do chao aqui e o rebatimento quente do asfalto
 * - e ele cancelava exatamente o azul do ceu, deixando a sombra acromatica. Pior:
 * esse rebatimento JA ESTA no environment map (o casco de solo de Sky.js), entao
 * estava sendo contado duas vezes.
 *
 * HEMI_GROUND_LEVEL corta a parcela repetida. Com o teto do ceu (Sky.js) o
 * environment tambem virou 55% mais forte do lado da sombra e azul de verdade
 * (B/R 2.0). Rodando o modelo ate o pixel, a parede na sombra sai de
 * 45,45,49 (B-R 4, o defeito) para 26,31,40 (B-R 14) com 51% da luminancia -
 * sombra mais fechada e AZUL, que e o que faz o quadro ler fim de tarde.
 * O piso interno, que estava branco-azulado, fica com 78% do valor de antes;
 * so isso nao resolve o interior (falta oclusao), mas para de lavar.
 *
 * AVISO PARA QUEM LER ESTE PARAGRAFO DEPOIS: o alvo "sombra AZUL de verdade"
 * descrito acima foi perseguido longe demais na rodada seguinte e virou o defeito
 * oposto - sombra azul-ciano chapada, com o material sumindo debaixo da cor da
 * luz. O alvo correto e sombra MAIS FRIA QUE A LUZ na comparacao, com B-R de uma
 * duzia de niveis na tela, nao de quarenta. Ver o bloco de numeros logo abaixo
 * antes de subir croma aqui de novo.
 */
/**
 * REBALANCO DE CROMA DA RODADA 3. Historico dos valores:
 *   HEMI_INTENSITY        0.48 -> 0.70 -> 0.45   (agora)
 *   HEMI_SKY_WHITE_MIX    0.26 -> 0.26 -> 0.78   (agora)
 *   HEMI_GROUND_LEVEL     0.20 -> 0.20 -> 0.31   (agora)
 *
 * O 0.70 com white mix 0.26 resolveu o breu (objeto escuro em sombra nao cai
 * mais para 1,4% de luminancia) e criou uma regressao pior: TODA area em sombra
 * ficou azul-ciano saturada - asfalto, parapeito e piso de terraco perdendo a
 * cor do material sob a cor da luz.
 *
 * A causa esta na conta, nao na intensidade. A cor do hemisferico sai do ZENITE
 * do modelo de Preetham (Sky.js), que com turbidity 8.5 / rayleigh 3.0 / sol a 15
 * graus vale (0.037, 0.119, 0.290) em radiancia. Normalizada pelo maior canal isso
 * e (0.127, 0.409, 1.000) - o azul mais saturado que existe neste modelo. Com
 * white mix 0.26 a cor da luz ficava (0.354, 0.563, 1.000), B/R = 2.8, e numa
 * superficie VOLTADA PARA CIMA (asfalto, piso de terraco) o hemisferico entrega
 * 100% desse azul, sem a metade quente do chao que salva as paredes verticais.
 * Somado ao environment map, que tambem era azul-dominante, o preenchimento de
 * uma superficie horizontal em sombra chegava a B/R = 3.4 - e o PostFX ainda
 * multiplica a croma da sombra por 1.12 e aplica split tone azul.
 *
 * As tres mudancas foram calculadas para MANTER A LUMINANCIA e cortar so a croma,
 * porque o breu nao pode voltar:
 *
 *  - white mix 0.26 -> 0.78: a cor vira (0.808, 0.870, 1.000), B/R = 1.24. Isso
 *    e luz de ceu de ~9000 K: mais fria que o sol (1, 0.56, 0.26) por uma margem
 *    enorme, mas ja neutra o bastante para o material aparecer por baixo. O
 *    clareamento sobe a luminancia da cor de 0.550 para 0.866 (x1.575) - e por
 *    isso a intensidade tem que descer na mesma proporcao, senao a sombra clareia.
 *
 *  - intensidade 0.70 -> 0.45: 0.70 x 0.550 = 0.385 contra 0.45 x 0.866 = 0.390.
 *    Ou seja, a superficie voltada para cima recebe a MESMA irradiancia de antes
 *    (+1,2%, dentro do ruido) com 27% da croma. O breu nao volta porque o nivel
 *    nao mudou.
 *
 *  - ground level 0.20 -> 0.31: o lobo do chao e multiplicado pela intensidade
 *    junto com o do ceu, e a intensidade caiu 36%. Sem compensar, a face de baixo
 *    de marquise, caixote e viga perderia um terco do rebatimento quente e
 *    escureceria sozinha. 0.70 x 0.20 = 0.140 contra 0.45 x 0.31 = 0.1395: o lobo
 *    do chao continua identico ao de antes.
 *
 * Resultado somado ao environment dessaturado (Sky.js ENV_SKY_SATURATION), no
 * estagio de luz, com a mesma luminancia de antes em todos os casos:
 *   superficie para cima em sombra   B/R 3.41 -> 1.36
 *   parede vertical em sombra        B/R 2.34 -> 1.15
 * Na tela, depois do split tone e do ganho de croma da sombra no PostFX, isso sai
 * de ~+46 niveis de azul sobre vermelho para ~+12: asfalto continua lendo asfalto,
 * so mais frio e mais escuro que o asfalto no sol.
 */
const HEMI_INTENSITY = 0.45;
/** Quanto a cor do zenite e clareada em direcao ao branco antes de virar luz.
 *  O zenite cru e (0.127, 0.409, 1.000) normalizado - azul de tinta, nao de luz
 *  ambiente. 0.78 deixa (0.808, 0.870, 1.000): esfria sem tingir. */
const HEMI_SKY_WHITE_MIX = 0.78;
const HEMI_GROUND_WHITE_MIX = 0.10;
/** Fracao mantida do rebatimento do chao: o resto ja vem do environment map.
 *  Subiu de 0.20 para 0.31 so para compensar a queda de HEMI_INTENSITY - o
 *  produto intensidade x nivel continua em 0.14, exatamente o de antes. */
const HEMI_GROUND_LEVEL = 0.31;

/** Fracao do tamanho do frustum jogada a frente do olhar. */
const SHADOW_FORWARD_BIAS = 0.18;
/** Constante de tempo do amortecimento do deslocamento (1/s). */
const SHADOW_FOLLOW_LERP = 4.5;
/** Quantizacao do eixo de profundidade, em metros. Evita tremida de contato. */
const SHADOW_DEPTH_SNAP = 0.5;

/**
 * Largura ALVO da penumbra, em metros. O raio da sombra e declarado em texels,
 * e o texel muda de 1.9 a 3.5 cm conforme o preset - com raio fixo a penumbra
 * mudava de tamanho junto com a qualidade. Declarando em metros, "sombra macia"
 * quer dizer a mesma coisa em todos os presets.
 *
 * 11 cm e o limite util: sao 5 amostras (Vogel + rotacao por pixel) no PCF do
 * three 0.185, e passar disso comeca a mostrar o padrao de amostragem.
 */
const SHADOW_PENUMBRA_M = 0.11;

const FLASH_ATTACK = 0.06;
const FLASH_DECAY_POWER = 2.2;
const FLASH_MAX_INTENSITY = 400;
const FLASH_MAX_DISTANCE = 60;
const FLASH_DEFAULT_COLOR = 0xffd9a0;

// temporarios de modulo - ZERO alocacao dentro de update()
const _fwd = new THREE.Vector3();
const _center = new THREE.Vector3();
const _snapped = new THREE.Vector3();
const _worldUp = new THREE.Vector3(0, 1, 0);

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

export class LightingSystem {
  constructor(ctx) {
    this.ctx = ctx;

    this.sun = null;
    this.sunTarget = null;
    this.hemi = null;

    this._sunDir = new THREE.Vector3(FALLBACK_SUN_DIR[0], FALLBACK_SUN_DIR[1], FALLBACK_SUN_DIR[2]).normalize();
    this._basisX = new THREE.Vector3(1, 0, 0);
    this._basisY = new THREE.Vector3(0, 1, 0);
    this._basisZ = new THREE.Vector3(0, 0, 1);
    this._followDir = new THREE.Vector3(0, 0, -1);

    this._extent = 60;
    this._texelWorld = 0.03;
    this._lightDistance = 100;
    this._intensityScale = 1;
    this._sunVersion = -1;

    /** Frustum ancorado no centro do cenario em vez de seguir a camera - so
     *  quando ele ja cobre a diagonal do mapa (ver _refreshCoverageMode). */
    this._staticFrustum = false;
    this._boundsCenter = new THREE.Vector3();
    this._mapDiagonal = 0;
    this._boundsPending = true;

    /** @type {{light: THREE.PointLight, active: boolean, time: number, duration: number, peak: number, age: number}[]} */
    this._pool = [];
    this._poolSize = 0;
    /** @type {THREE.Light[]} luzes persistentes registradas por outros sistemas */
    this._localLights = [];

    this._offSettings = null;
    this._warned = false;
  }

  async init() {
    const ctx = this.ctx;
    const scene = ctx.scene;

    // --- sol ---------------------------------------------------------------
    this.sun = new THREE.DirectionalLight(0xffffff, 1);
    this.sun.name = 'sun';
    this.sunTarget = new THREE.Object3D();
    this.sunTarget.name = 'sunTarget';
    scene.add(this.sunTarget);
    this.sun.target = this.sunTarget;
    scene.add(this.sun);

    // --- preenchimento -----------------------------------------------------
    this.hemi = new THREE.HemisphereLight(0xffffff, 0xffffff, HEMI_INTENSITY);
    this.hemi.name = 'hemiFill';
    scene.add(this.hemi);

    this._syncWithSky();
    this._applyShadow();
    this._buildPool();
    this._updateShadowFollow(1 / 60, true);

    this._offSettings = ctx.settings?.onChange?.((key) => {
      if (key === 'quality' || key === 'shadowMapSize' || key === 'shadowDistance' || key === 'shadowsEnabled') {
        this._applyShadow();
        this._buildPool();
      }
    }) || null;
  }

  // ------------------------------------------------------------------------
  // Sol e ceu
  // ------------------------------------------------------------------------

  /** Le direcao, cor e intensidade do ceu. Barato - so roda quando o sol muda. */
  _syncWithSky() {
    const sky = this.ctx.sky;

    const dir = sky?.getSunDirection?.();
    if (dir && Number.isFinite(dir.x)) this._sunDir.copy(dir);
    else this._sunDir.set(FALLBACK_SUN_DIR[0], FALLBACK_SUN_DIR[1], FALLBACK_SUN_DIR[2]);
    if (this._sunDir.lengthSq() < 1e-6) this._sunDir.set(0, 1, 0);
    this._sunDir.normalize();
    // sol a pino deixa o lookAt da camera de sombra degenerado (paralelo ao up)
    if (Math.abs(this._sunDir.y) > 0.9995) {
      this._sunDir.x += 0.02;
      this._sunDir.normalize();
    }
    this._rebuildBasis();

    const col = sky?.getSunColor?.();
    if (col && Number.isFinite(col.r)) this.sun.color.copy(col);
    else this.sun.color.setRGB(FALLBACK_SUN_RGB[0], FALLBACK_SUN_RGB[1], FALLBACK_SUN_RGB[2]);

    const inten = sky?.getSunIntensity?.();
    const base = (typeof inten === 'number' && inten > 0 && Number.isFinite(inten)) ? inten : FALLBACK_SUN_INTENSITY;
    this.sun.intensity = base * this._intensityScale;

    this._applyHemisphereColors();
    this._sunVersion = sky?.sunVersion ?? 0;
  }

  /**
   * Cores do hemisferico saem do ceu, mas NORMALIZADAS: as cores do modelo sao
   * radiancias (0.03 a 0.3) e virariam luz preta se usadas cruas. O que interessa
   * aqui e o matiz - frio vindo de cima, quente rebatendo do chao.
   */
  _applyHemisphereColors() {
    const sky = this.ctx.sky;
    const zen = sky?.getZenithColor?.();
    const gnd = sky?.getGroundBounceColor?.();

    if (zen && Number.isFinite(zen.r)) {
      this._normalizeInto(this.hemi.color, zen.r, zen.g, zen.b, HEMI_SKY_WHITE_MIX, 1);
    } else {
      // mesmo resultado que o zenite do modelo passado por HEMI_SKY_WHITE_MIX
      // 0.78: se o fallback continuasse no azul saturado antigo (0.35, 0.56, 1.0)
      // a cena piscaria de azul no primeiro frame antes do ceu assumir
      this.hemi.color.setRGB(0.81, 0.87, 1.0);
    }
    if (gnd && Number.isFinite(gnd.r)) {
      this._normalizeInto(this.hemi.groundColor, gnd.r, gnd.g, gnd.b, HEMI_GROUND_WHITE_MIX, HEMI_GROUND_LEVEL);
    } else {
      this.hemi.groundColor.setRGB(1.0 * HEMI_GROUND_LEVEL, 0.79 * HEMI_GROUND_LEVEL, 0.62 * HEMI_GROUND_LEVEL);
    }
    this.hemi.intensity = HEMI_INTENSITY;
  }

  /** Normaliza (so o matiz interessa), clareia em direcao ao branco e aplica o
   *  nivel final. level < 1 e o que evita contar o rebatimento do chao duas
   *  vezes (aqui e no environment map). */
  _normalizeInto(target, r, g, b, whiteMix, level) {
    const mx = Math.max(r, g, b, 1e-6);
    let nr = r / mx;
    let ng = g / mx;
    let nb = b / mx;
    nr += (1 - nr) * whiteMix;
    ng += (1 - ng) * whiteMix;
    nb += (1 - nb) * whiteMix;
    const k = level === undefined ? 1 : level;
    target.setRGB(nr * k, ng * k, nb * k);
  }

  /**
   * Base ortonormal da camera de sombra, identica a que o three monta em
   * LightShadow.updateMatrices via lookAt(target) com up = +Y:
   *   z = normalize(posicao - alvo) = direcao do sol
   *   x = normalize(cross(up, z))
   *   y = cross(z, x)
   * O arredondamento em texels PRECISA acontecer nesta base, senao nao alinha
   * com a grade da textura de sombra e o tremido continua.
   */
  _rebuildBasis() {
    this._basisZ.copy(this._sunDir);
    this._basisX.crossVectors(_worldUp, this._basisZ);
    if (this._basisX.lengthSq() < 1e-8) this._basisX.set(1, 0, 0);
    this._basisX.normalize();
    this._basisY.crossVectors(this._basisZ, this._basisX).normalize();
  }

  // ------------------------------------------------------------------------
  // Sombra
  // ------------------------------------------------------------------------

  _applyShadow() {
    const s = this.ctx.settings || {};
    const sun = this.sun;
    if (!sun) return;

    sun.castShadow = s.shadowsEnabled !== false;

    const mapSize = clamp(Math.round(s.shadowMapSize || 2048), 512, 4096);

    /**
     * COBERTURA DO FRUSTUM. Era `shadowDistance * 0.58` limitado a 76 m: no preset
     * high dava 69,6 m de lado (meia largura 34,8 m) e, com o centro jogado 12,5 m
     * a frente do olhar (SHADOW_FORWARD_BIAS), a caixa ia de ~22 m atras da camera
     * ate ~47 m a frente. Medido nos prints da rodada 2: na praca batida de sol,
     * poste de 6 m, soldado, cavalo-de-frisa e caixote, NENHUM com sombra no chao,
     * enquanto o predio 20 m mais perto projetava terminador duro no mesmo quadro.
     * Um mapa de 90 x 90 m visto de uma laje tem diagonal de ~127 m - mais da
     * metade do que aparecia no quadro estava fora do frustum.
     *
     * Agora o lado do frustum e a PROPRIA shadowDistance do preset (60 / 90 / 120 /
     * 160 m), sem o fator 0.58. Assim Settings continua sendo a fonte do custo
     * grafico - NAO ha override de shadowMapSize aqui, que seria memoria de video
     * a mais sem o usuario pedir. O preco e texel mais grosso: 5,9 cm no high
     * (120 m em 2048) contra 3,4 cm de antes, 3,9 cm no ultra (160 m em 4096).
     *
     * A penumbra nao muda de tamanho porque e declarada em metros
     * (SHADOW_PENUMBRA_M). O que PRECISA acompanhar o texel e o bias: ver logo
     * abaixo, senao o texel maior traz de volta o acne no chao rasante.
     */
    const extent = clamp(s.shadowDistance || 90, 60, 160);
    this._extent = extent;
    this._texelWorld = extent / mapSize;
    this._refreshCoverageMode();

    const half = extent * 0.5;
    const halfDepth = extent * 0.75 + 30;   // cobre predios ate ~35 m de altura
    this._lightDistance = halfDepth + 10;

    const shadow = sun.shadow;
    if (shadow.mapSize.x !== mapSize || shadow.mapSize.y !== mapSize) {
      shadow.mapSize.set(mapSize, mapSize);
      if (shadow.map) {
        shadow.map.dispose();
        shadow.map = null;   // o three recria no tamanho novo
      }
    }

    const cam = shadow.camera;
    cam.left = -half;
    cam.right = half;
    cam.top = half;
    cam.bottom = -half;
    cam.near = 5;
    cam.far = this._lightDistance + halfDepth;
    cam.updateProjectionMatrix();

    /**
     * RAIO. A sombra saia navalha em qualquer distancia, e o motivo NAO era o
     * radius ser ignorado: conferido no fonte instalado
     * (three/src/renderers/shaders/ShaderChunk/shadowmap_pars_fragment.glsl.js,
     * ramo SHADOWMAP_TYPE_PCF), o PCF do three 0.185 usa shadowRadius sim - ele
     * escala um disco de Vogel de 5 amostras girado por pixel. O que estava
     * errado era o VALOR: radius 2 sobre texel de 2.5 cm da 5 cm de penumbra,
     * que a 1080p e menos de um pixel. Declarando a penumbra em METROS o raio
     * sai certo em qualquer preset.
     *
     * O que o PCF nao faz e penumbra que CRESCE com a distancia ao bloqueador -
     * isso e propriedade do VSM, e depende de renderer.shadowMap.type
     * (src/render/Renderer.js), que nao e deste modulo. Os dois ramos abaixo
     * ficam calibrados para o caso de aquele arquivo trocar para VSM.
     */
    const radius = clamp(SHADOW_PENUMBRA_M / Math.max(1e-4, this._texelWorld), 2, 8);

    /**
     * BIAS. O sol esta a 15 graus, entao quase todo chao recebe a luz de raspao -
     * o pior caso possivel para shadow acne: dentro de UM texel a profundidade do
     * chao varia texel * tan(75 graus), ou seja quase 4 vezes o tamanho do texel.
     * Sem bias, o chao inteiro fica listrado.
     *
     * Duas defesas somadas, cada uma com o defeito da outra sob controle:
     *  - bias de profundidade declarado em METROS e convertido para a escala
     *    normalizada da camera. Declarar em metros e o que mantem o ajuste valido
     *    quando o preset muda o frustum; o valor sai do texel (ver biasMeters
     *    abaixo) e deixa uma fenda de peter-panning da mesma ordem.
     *  - normalBias de ~2 texels, que desloca a AMOSTRA ao longo da normal e
     *    escapa da quantizacao sem mexer na profundidade comparada (nao causa
     *    peter-panning; no maximo encolhe a sombra em 2 texels, ~7 cm).
     */
    const depthRange = Math.max(1, cam.far - cam.near);

    /**
     * O bias em METROS agora sai do TEXEL, nao de um numero fixo. Dentro de um
     * texel a profundidade do chao varia texel * tan(75 graus) = 3.73 texels; 45%
     * disso cancela o acne e deixa fenda da mesma ordem. No texel antigo (3,4 cm)
     * a formula devolve 5,7 cm, praticamente o 5,5 cm que ja estava calibrado -
     * ou seja, nao muda o que estava bom e acompanha o frustum maior.
     */
    const biasMeters = clamp(this._texelWorld * 3.73 * 0.45, 0.05, 0.14);

    const vsm = this.ctx.renderer?.shadowMap?.type === THREE.VSMShadowMap;
    if (vsm) {
      // VSM compara variancia, nao profundidade: quase imune a acne
      shadow.bias = -0.012 / depthRange;
      shadow.normalBias = Math.min(this._texelWorld * 1.2, 0.05);
      // no VSM o raio e o borrao aplicado no mapa de momentos, nao um filtro por
      // pixel: pode ser maior sem custo por fragmento, mas vaza luz em vao
      // estreito se exagerar (conferir as janelas do armazem antes de subir)
      shadow.radius = Math.min(radius * 0.8, 3.5);
      shadow.blurSamples = 8;
    } else {
      shadow.bias = -biasMeters / depthRange;
      // 2 texels, com teto de 7,5 cm: o normalBias encolhe a sombra, e com texel
      // grosso 2 texels dariam 12 cm - o bastante para sumir com a sombra de
      // contato de caixote e cavalo-de-frisa
      shadow.normalBias = Math.min(this._texelWorld * 2.0, 0.075);
      shadow.radius = radius;
    }
  }

  /**
   * Decide se o frustum SEGUE a camera ou fica ancorado no centro do cenario.
   *
   * Quando o lado do frustum ja cobre a diagonal do mapa, seguir a camera nao
   * ganha area nenhuma e ainda arrasta a regiao de sombra quando o jogador gira.
   * Ancorado no centro, o cenario INTEIRO projeta sombra e o frustum para de se
   * mover - zero tremida por movimento, e o poste no fundo da praca tem sombra
   * seja de que ponto de vista for.
   *
   * ctx.level nasce DEPOIS deste sistema (ordem do boot em main.js), por isso a
   * consulta fica pendente e e repetida no update() ate os limites existirem.
   * Nao aloca: getCenter escreve num Vector3 do proprio sistema.
   */
  _refreshCoverageMode() {
    const b = this.ctx.level?.bounds;
    if (!b || typeof b.getCenter !== 'function' || (b.isEmpty && b.isEmpty())) {
      this._staticFrustum = false;
      this._boundsPending = true;
      return;
    }
    b.getCenter(this._boundsCenter);
    const dx = b.max.x - b.min.x;
    const dz = b.max.z - b.min.z;
    this._mapDiagonal = Math.sqrt(dx * dx + dz * dz);
    this._staticFrustum = this._extent >= this._mapDiagonal * 1.02;
    this._boundsPending = false;
  }

  /**
   * Reposiciona o frustum da sombra em torno do jogador, arredondado em texels.
   * Chamado todo frame; nao aloca nada.
   */
  _updateShadowFollow(dt, snapNow) {
    const sun = this.sun;
    const camera = this.ctx.camera;
    if (!sun || !camera) return;

    camera.getWorldDirection(_fwd);
    _fwd.y = 0;
    const len = _fwd.length();
    if (len > 1e-4) _fwd.multiplyScalar(1 / len);
    else _fwd.set(0, 0, -1);

    if (snapNow) {
      this._followDir.copy(_fwd);
    } else {
      const k = 1 - Math.exp(-SHADOW_FOLLOW_LERP * dt);
      this._followDir.lerp(_fwd, k);
      if (this._followDir.lengthSq() < 1e-6) this._followDir.copy(_fwd);
      else this._followDir.normalize();
    }

    _center.copy(camera.position).addScaledVector(this._followDir, this._extent * SHADOW_FORWARD_BIAS);
    // frustum que ja cobre o mapa inteiro nao segue ninguem: centro fixo
    if (this._staticFrustum) _center.copy(this._boundsCenter);

    // projeta o centro na base da luz e quantiza os dois eixos da textura
    const t = this._texelWorld;
    const px = Math.round(_center.dot(this._basisX) / t) * t;
    const py = Math.round(_center.dot(this._basisY) / t) * t;
    const pz = Math.round(_center.dot(this._basisZ) / SHADOW_DEPTH_SNAP) * SHADOW_DEPTH_SNAP;

    _snapped.set(0, 0, 0)
      .addScaledVector(this._basisX, px)
      .addScaledVector(this._basisY, py)
      .addScaledVector(this._basisZ, pz);

    this.sunTarget.position.copy(_snapped);
    this.sun.position.copy(_snapped).addScaledVector(this._sunDir, this._lightDistance);
  }

  // ------------------------------------------------------------------------
  // Pool de luzes dinamicas
  // ------------------------------------------------------------------------

  _buildPool() {
    const quality = this.ctx.settings?.quality || 'high';
    const wanted = clamp(DYNAMIC_BY_QUALITY[quality] ?? MAX_DYNAMIC_LIGHTS, 1, MAX_DYNAMIC_LIGHTS);
    if (wanted === this._poolSize) return;

    const scene = this.ctx.scene;
    while (this._pool.length > wanted) {
      const slot = this._pool.pop();
      scene.remove(slot.light);
      slot.light.dispose?.();
    }
    while (this._pool.length < wanted) {
      const light = new THREE.PointLight(FLASH_DEFAULT_COLOR, 0, 12, 2);
      light.name = 'dynamicFlash';
      light.castShadow = false;
      // fica sempre na cena: mexer no .visible mudaria a contagem de luzes e
      // faria o three recompilar todos os shaders no meio do tiroteio
      light.visible = true;
      scene.add(light);
      this._pool.push({ light, active: false, time: 0, duration: 0.05, peak: 0, age: 0 });
    }
    this._poolSize = this._pool.length;
  }

  /**
   * Luz curta de efeito (muzzle flash, explosao). Reaproveita a mais antiga
   * quando o pool estoura.
   *
   * @param {{x:number,y:number,z:number}} position
   * @param {number|string|THREE.Color} [color]
   * @param {number} [intensity]
   * @param {number} [distance]  raio de alcance em metros
   * @param {number} [durationSec]
   */
  flash(position, color, intensity, distance, durationSec) {
    if (!position || this._pool.length === 0) return null;

    let slot = null;
    for (let i = 0; i < this._pool.length; i++) {
      if (!this._pool[i].active) { slot = this._pool[i]; break; }
    }
    if (!slot) {
      // rouba a que esta mais perto de acabar (maior progresso, nao maior tempo:
      // uma explosao de 0.6 s nao pode perder a vez para um clarao de 0.05 s)
      let oldest = 0;
      let best = this._pool[0].time / this._pool[0].duration;
      for (let i = 1; i < this._pool.length; i++) {
        const p = this._pool[i].time / this._pool[i].duration;
        if (p > best) { best = p; oldest = i; }
      }
      slot = this._pool[oldest];
    }

    const light = slot.light;
    light.position.set(position.x || 0, position.y || 0, position.z || 0);
    if (color === undefined || color === null) light.color.setHex(FLASH_DEFAULT_COLOR);
    else if (color.isColor) light.color.copy(color);
    else light.color.set(color);

    const peak = clamp(Number.isFinite(intensity) ? intensity : 14, 0, FLASH_MAX_INTENSITY);
    light.distance = clamp(Number.isFinite(distance) ? distance : 10, 0.5, FLASH_MAX_DISTANCE);
    light.decay = 2;

    slot.peak = peak;
    slot.duration = clamp(Number.isFinite(durationSec) ? durationSec : 0.05, 0.01, 3);
    slot.time = 0;
    slot.active = true;
    light.intensity = peak;
    return light;
  }

  /**
   * Registra uma luz persistente criada por outro sistema (poste, fogo, etc).
   * Chamar na carga do cenario, NUNCA por frame: cada mudanca na contagem de
   * luzes recompila os shaders de toda a cena.
   */
  addLocalLight(light) {
    if (!light || !light.isLight) return null;
    if (this._localLights.indexOf(light) !== -1) return light;
    this._localLights.push(light);
    if (!light.parent) this.ctx.scene.add(light);
    if (this._localLights.length + this._poolSize > 12 && !this._warned) {
      this._warned = true;
      console.warn('[Lighting] mais de 12 luzes na cena - custo por fragmento fica alto');
    }
    return light;
  }

  removeLocalLight(light) {
    const i = this._localLights.indexOf(light);
    if (i === -1) return false;
    this._localLights.splice(i, 1);
    this.ctx.scene.remove(light);
    return true;
  }

  _updateFlashes(dt) {
    for (let i = 0; i < this._pool.length; i++) {
      const slot = this._pool[i];
      if (!slot.active) continue;
      slot.time += dt;
      const u = slot.time / slot.duration;
      if (u >= 1) {
        slot.active = false;
        slot.time = 0;
        slot.light.intensity = 0;
        continue;
      }
      // ataque quase instantaneo e queda com cauda: e assim que um clarao le
      const k = u < FLASH_ATTACK
        ? u / FLASH_ATTACK
        : Math.pow(1 - (u - FLASH_ATTACK) / (1 - FLASH_ATTACK), FLASH_DECAY_POWER);
      slot.light.intensity = slot.peak * k;
    }
  }

  // ------------------------------------------------------------------------
  // Frame
  // ------------------------------------------------------------------------

  update(dt) {
    if (!this.sun) return;

    const version = this.ctx.sky?.sunVersion;
    if (version !== undefined && version !== this._sunVersion) this._syncWithSky();

    // o cenario carrega depois deste sistema: enquanto nao houver limites, tenta
    // de novo (duas leituras por frame, e para de vez quando resolver)
    if (this._boundsPending) this._refreshCoverageMode();

    this._updateShadowFollow(dt, false);
    this._updateFlashes(dt);
  }

  // ------------------------------------------------------------------------
  // API publica
  // ------------------------------------------------------------------------

  /** Vetor unitario apontando PARA o sol. Nao mutar. */
  getSunDirection() {
    return this._sunDir;
  }

  /** Multiplicador aplicado sobre a intensidade sugerida pelo ceu. */
  setIntensityScale(scale) {
    this._intensityScale = clamp(scale, 0, 8);
    this._syncWithSky();
  }

  setShadowsEnabled(on) {
    if (this.sun) this.sun.castShadow = !!on;
  }

  /** Tamanho atual do frustum de sombra, em metros. Util para diagnostico. */
  getShadowExtent() {
    return this._extent;
  }

  dispose() {
    const scene = this.ctx.scene;
    this._offSettings?.();
    this._offSettings = null;

    for (let i = 0; i < this._pool.length; i++) {
      scene.remove(this._pool[i].light);
      this._pool[i].light.dispose?.();
    }
    this._pool.length = 0;
    this._poolSize = 0;

    for (let i = 0; i < this._localLights.length; i++) scene.remove(this._localLights[i]);
    this._localLights.length = 0;

    if (this.sun) {
      if (this.sun.shadow?.map) {
        this.sun.shadow.map.dispose();
        this.sun.shadow.map = null;
      }
      scene.remove(this.sun);
      this.sun.dispose?.();
      this.sun = null;
    }
    if (this.sunTarget) {
      scene.remove(this.sunTarget);
      this.sunTarget = null;
    }
    if (this.hemi) {
      scene.remove(this.hemi);
      this.hemi.dispose?.();
      this.hemi = null;
    }
  }
}
